import { useAppSelector } from '@src/core/hooks';
import type { Cart } from '@src/domains/cart';
import {
  canManageCartCoupon,
  isCartSummaryPresentation,
  isSummaryReady,
  resolveSummarySource,
  type SummarySource,
  type Transactable,
} from '@src/domains/transactables';
import { useTransactable } from '@src/features/transactables/hooks/useTransactable';

export type UseTransactableSummaryResult = {
  source: SummarySource;
  transactable: Transactable;
  cart: Cart | undefined;
  isLoading: boolean;
  isReady: boolean;
  canManageCartCoupon: boolean;
};

export const useTransactableSummary = (): UseTransactableSummaryResult => {
  const transactable = useTransactable();
  const cart = useAppSelector((state) => state.cart.cart);
  const isLoadingCart = useAppSelector((state) => state.cart.isLoadingCart);
  const hasCartItems = (cart?.items?.length ?? 0) > 0;

  const source = resolveSummarySource(transactable);
  return {
    source,
    transactable,
    cart,
    // The cart slice starts as loading, and sale and payment links never load a cart.
    isLoading: isLoadingCart && isCartSummaryPresentation(source, transactable),
    isReady: isSummaryReady(source, transactable, hasCartItems),
    canManageCartCoupon: canManageCartCoupon(source, transactable),
  };
};

export default useTransactableSummary;
